import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { brl } from "@/lib/format";
import { toast } from "sonner";
import { FileText, CheckCircle2, AlertCircle, Copy, ExternalLink, Printer, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

const STATUS_LABEL = {
  pending: { label: "Em aberto", cls: "bg-amber-500/10 text-amber-700 border-amber-500/30" },
  paid: { label: "Paga", cls: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30" },
  exempt: { label: "Isenta", cls: "bg-sky-500/10 text-sky-700 border-sky-500/30" },
  test: { label: "Teste", cls: "bg-muted text-muted-foreground border-border" },
  overdue: { label: "Vencida", cls: "bg-red-500/10 text-red-700 border-red-500/30" },
};

function fmtBr(iso) {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function fmtPeriod(yyyymm) {
  if (!yyyymm) return "—";
  const [y, m] = yyyymm.split("-");
  const names = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
  return `${names[Number(m) - 1] || m}/${y}`;
}

export default function Invoices() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/billing/invoices");
      // Auto-flag overdue on the client for pending items with past due_date.
      const now = new Date().toISOString().slice(0, 10);
      setItems((data || []).map((i) => (i.status === "pending" && i.due_date < now ? { ...i, status: "overdue" } : i)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao carregar"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const pending = items.filter((i) => i.status === "pending" || i.status === "overdue");
  const paid = items.filter((i) => i.status === "paid" || i.status === "exempt");
  const totalPending = pending.reduce((s, i) => s + Number(i.amount || 0), 0);

  const copyPix = async (code) => {
    try { await navigator.clipboard.writeText(code); toast.success("Código PIX copiado!"); }
    catch { toast.error("Não foi possível copiar"); }
  };

  return (
    <div className="max-w-5xl mx-auto py-8 px-4 space-y-8">
      <div>
        <h1 className="font-display text-3xl font-semibold flex items-center gap-3"><FileText className="h-7 w-7 text-primary" /> Minhas faturas</h1>
        <p className="text-muted-foreground mt-1">Mensalidade da franquia — acompanhe pagamentos e boletos em aberto.</p>
      </div>

      {pending.length > 0 && (
        <div data-testid="invoices-pending-section" className="surface p-6 space-y-4 border-amber-500/30">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 className="font-display font-semibold text-lg flex items-center gap-2"><AlertCircle className="h-5 w-5 text-amber-500" /> Em aberto</h2>
              <p className="text-sm text-muted-foreground">{pending.length} fatura(s) · Total {brl(totalPending)}</p>
            </div>
          </div>
          <div className="grid gap-3">
            {pending.map((i) => (
              <div key={i.id} data-testid={`invoice-${i.id}`} className={`rounded-lg border p-4 space-y-3 ${STATUS_LABEL[i.status].cls}`}>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <div className="text-lg font-semibold text-foreground">Fatura {fmtPeriod(i.period)}</div>
                    <div className="text-sm text-muted-foreground">Vencimento: <span className="font-medium text-foreground">{fmtBr(i.due_date)}</span></div>
                    {i.note && <div className="text-xs text-muted-foreground mt-1">{i.note}</div>}
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-foreground">{brl(i.amount)}</div>
                    <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full border ${STATUS_LABEL[i.status].cls}`}>{STATUS_LABEL[i.status].label}</span>
                  </div>
                </div>
                {(i.asaas_invoice_url || i.asaas_pix_copy_paste) && (
                  <div className="flex items-center flex-wrap gap-2 pt-2 border-t border-current/10">
                    {i.asaas_invoice_url && (
                      <a href={i.asaas_invoice_url} target="_blank" rel="noreferrer" data-testid={`invoice-pay-${i.id}`}>
                        <Button variant="default" size="sm"><ExternalLink className="h-4 w-4 mr-1" /> Pagar via Asaas</Button>
                      </a>
                    )}
                    {i.asaas_invoice_url && (
                      <Button variant="outline" size="sm" onClick={() => window.open(i.asaas_invoice_url, "_blank")} data-testid={`invoice-view-${i.id}`} title="Abrir cobrança do Asaas em nova aba (use o botão Imprimir da página do Asaas)">
                        <Printer className="h-4 w-4 mr-1" /> Visualizar / Imprimir
                      </Button>
                    )}
                    {i.asaas_bank_slip_url && (
                      <a href={i.asaas_bank_slip_url} target="_blank" rel="noreferrer" data-testid={`invoice-boleto-${i.id}`}>
                        <Button variant="outline" size="sm"><Download className="h-4 w-4 mr-1" /> Baixar boleto (PDF)</Button>
                      </a>
                    )}
                    {i.asaas_pix_copy_paste && (
                      <Button variant="outline" size="sm" onClick={() => copyPix(i.asaas_pix_copy_paste)} data-testid={`invoice-copy-pix-${i.id}`}>
                        <Copy className="h-4 w-4 mr-1" /> Copiar PIX
                      </Button>
                    )}
                  </div>
                )}
                {!i.asaas_invoice_url && (
                  <div className="text-xs text-muted-foreground italic border-t border-current/10 pt-2">Aguardando emissão do boleto/PIX pelo administrador da plataforma.</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="surface p-6 space-y-4">
        <h2 className="font-display font-semibold text-lg flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-500" /> Histórico ({paid.length})</h2>
        {loading ? (
          <div className="text-sm text-muted-foreground">Carregando…</div>
        ) : paid.length === 0 ? (
          <div className="text-sm text-muted-foreground">Nenhuma fatura paga ainda.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground bg-muted/30">
                <tr>
                  <th className="py-2 px-3">Período</th>
                  <th className="py-2 px-3">Vencimento</th>
                  <th className="py-2 px-3">Pago em</th>
                  <th className="py-2 px-3">Via</th>
                  <th className="py-2 px-3 text-right">Valor</th>
                  <th className="py-2 px-3 text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {paid.map((i) => (
                  <tr key={i.id} data-testid={`invoice-paid-${i.id}`} className="border-t border-border">
                    <td className="py-2 px-3 font-medium">{fmtPeriod(i.period)}</td>
                    <td className="py-2 px-3">{fmtBr(i.due_date)}</td>
                    <td className="py-2 px-3">{fmtBr(i.paid_at)}</td>
                    <td className="py-2 px-3 text-xs text-muted-foreground">{i.paid_via || "—"}</td>
                    <td className="py-2 px-3 text-right font-medium">{i.status === "exempt" ? "Isenta" : brl(i.amount)}</td>
                    <td className="py-2 px-3 text-right"><span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_LABEL[i.status].cls}`}>{STATUS_LABEL[i.status].label}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
